'use client'

import { useState, FormEvent } from 'react'
import Image from 'next/image'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json() as { success?: boolean; error?: string }
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f172a] px-4">
      <div className="w-full max-w-md">
        {/* Logo / Wordmark */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Image
              src="/ingen-studio-logo.svg"
              alt="InGen Studio"
              width={200}
              height={36}
              priority
            />
          </div>
          <p className="text-[#64748b] text-sm">You shoot it. We finish it.</p>
        </div>

        {/* Card */}
        <div className="bg-[#1e293b] border border-white/[0.06] rounded-2xl p-8">
          {sent ? (
            <div className="text-center space-y-3">
              <div className="text-3xl">📬</div>
              <h2 className="text-[#f8fafc] font-semibold text-lg">Check your email</h2>
              <p className="text-[#94a3b8] text-sm">
                We sent a magic link to{' '}
                <span className="text-[#f8fafc] font-medium">{email}</span>.
                Click it to sign in.
              </p>
              <button
                onClick={() => { setSent(false); setEmail('') }}
                className="text-xs text-[#10b981] hover:text-[#34d399] mt-4 transition-colors"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-[#f8fafc] font-semibold text-lg mb-1">Sign in</h2>
              <p className="text-[#64748b] text-sm mb-6">
                Enter your email and we&apos;ll send you a magic link.
              </p>

              <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-xs text-[#94a3b8] mb-1.5 font-medium">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-2.5 text-[#f8fafc] placeholder:text-[#475569] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-xs bg-red-950/40 border border-red-900/50 rounded px-3 py-2">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
                >
                  {loading ? 'Sending…' : 'Send Magic Link'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-[#334155] mt-6">
          The InGen Corporation
        </p>
      </div>
    </div>
  )
}
